import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronDown,
  BarChart3,
  Layers,
  CalendarCheck,
  Settings,
  ClipboardList,
  AlertTriangle,
  Info,
  CheckCircle2,
  FileText,
  Download,
} from "lucide-react";
import "../../styles/Help.css";
import "../../styles/attendanceHelp.css";

/* ------------------------------------------------------------------ */
/*  Data — sourced from actual project files                          */
/* ------------------------------------------------------------------ */

const REPORT_CATEGORIES = [
  { name: "Employee Reports", count: 4, color: "ic-blue" },
  { name: "Attendance Reports", count: 5, color: "ic-green" },
  { name: "Leave Reports", count: 4, color: "ic-teal" },
  { name: "Payroll Reports", count: 6, color: "ic-rose" },
  { name: "Statutory Reports", count: 3, color: "ic-amber" },
  { name: "Banking Reports", count: 7, color: "ic-violet" },
  { name: "Analytics Reports", count: 1, color: "ic-blue" },
  { name: "Government Reports", count: 2, color: "ic-slate" },
  { name: "Compliance Reports", count: 0, color: "ic-slate" },
  { name: "Custom Reports", count: 0, color: "ic-slate", soon: true },
];

const EMPLOYEE_REPORTS = [
  { name: "Employee Master Report", desc: "Complete list of all employees with org details", filters: "Employee, Department, Designation, Status", export: "View, Excel" },
  { name: "Employee Directory", desc: "Employee contact and department details", filters: "Department, Designation", export: "View, Excel" },
  { name: "Joining Report", desc: "Employees joined during the selected period", filters: "From Date, To Date, Department", export: "View, Excel" },
  { name: "Exit / Inactive Employee Report", desc: "Resigned and inactive employees in the selected period", filters: "Date Range, From Date, To Date", export: "View, Excel" },
];

const ATTENDANCE_REPORTS = [
  { name: "Daily Attendance Report", desc: "Daily attendance details for selected employees", filters: "Date, Department, Employee", export: "View, Excel, PDF" },
  { name: "Monthly Attendance Summary", desc: "Monthly attendance summary by department", filters: "Month, Year, Department", export: "View, Excel, PDF" },
  { name: "Absent Report", desc: "Employees marked absent in the selected period", filters: "From Date, To Date, Department", export: "View, Excel" },
  { name: "Late Coming Report", desc: "Employees arriving late during the selected period", filters: "From Date, To Date, Department", export: "View, Excel" },
  { name: "Attendance Register", desc: "Monthly attendance sheet with day-wise status (P/A/L/H)", filters: "Month, Year, Department", export: "View, Excel, PDF" },
];

const LEAVE_REPORTS = [
  { name: "Leave Balance Report", desc: "Current leave balance of employees", filters: "Department, Leave Type", export: "Excel, CSV, PDF, Print" },
  { name: "Leave Register", desc: "All leave transactions for the selected period", filters: "Employee (searchable), From Date, To Date, Leave Type", export: "Excel, CSV, Print" },
  { name: "Leave Utilization Report", desc: "Leave availed versus remaining balance by department", filters: "Year, Department", export: "Excel, CSV, PDF, Print" },
  { name: "Pending Leave Requests", desc: "Pending leave approval requests awaiting action", filters: "Department, Approver (All / Reporting Manager / HR Approver)", export: "Excel, CSV, Print" },
];

const PAYROLL_REPORTS = [
  { name: "Employee Salary Register", desc: "Full monthly breakup per employee from frozen payroll snapshots", filters: "Payroll Run, Financial Year, Department, Search, Include Resigned", export: "Excel, CSV, PDF, Print", dedicated: true },
  { name: "Payroll Register", desc: "Summary of gross, deductions, and net pay per employee", filters: "Payroll Month, Department", export: "Excel, CSV, PDF, Print" },
  { name: "Salary Register", desc: "Detailed breakup — Basic, HRA, allowances, statutory splits", filters: "Payroll Month, Department", export: "Excel, CSV, PDF, Print" },
  { name: "Payroll Summary", desc: "Single-row aggregate totals for the payroll run", filters: "Payroll Month", export: "Excel, CSV, PDF, Print" },
  { name: "Bank Transfer Report", desc: "Net salary with bank account and IFSC for disbursement", filters: "Payroll Month, Department", export: "Excel, CSV, PDF, Print" },
  { name: "LOP Report", desc: "Loss-of-pay days and recovered amount per employee", filters: "Payroll Month, Department", export: "Excel, CSV, PDF, Print" },
];

const STATUTORY_REPORTS = [
  { name: "PF Report", desc: "Provident Fund — employee and employer contributions", filters: "Payroll Month, Department", export: "Excel, CSV, PDF, Print" },
  { name: "ESI Report", desc: "Monthly ESIC contribution report from finalized payroll", filters: "Period, Month, Year, Department, Designation, Location, Status, ESI Eligible, and more", export: "Excel, CSV, PDF, Print", dedicated: true },
  { name: "TDS Report", desc: "Taxable income and TDS deducted per employee", filters: "Payroll Month, Department, Calendar Month, Financial Year, Search", export: "Excel, CSV, Print" },
];

const BANKING_REPORTS = [
  { name: "Bank Transfer Register", desc: "Employee bank details, amounts, status, and UTR numbers" },
  { name: "Salary Payment Register", desc: "Payment totals with success/failure counts per employee" },
  { name: "Failed Payments", desc: "Failed payment attempts with reason and retry count" },
  { name: "Pending Payments", desc: "Payments awaiting processing" },
  { name: "Bank-wise Summary", desc: "Aggregated amounts grouped by bank" },
  { name: "Branch-wise Summary", desc: "Amounts grouped by bank branch and IFSC" },
  { name: "Department-wise Summary", desc: "Payment amounts grouped by department" },
];

const GOVT_REPORTS = [
  { name: "TDS Compliance Center", desc: "TDS compliance and filing workspace" },
  { name: "Form 16 (Statutory)", desc: "Annual Form 16 generation for employees" },
];

const STATUTORY_WORKSPACE_TABS = [
  { tab: "Preview", desc: "Data grid with KPI summary cards, sortable table with pagination" },
  { tab: "Exceptions", desc: "Validation exceptions and issues detected in payroll data" },
  { tab: "Download History", desc: "Previously downloaded reports with re-download capability" },
  { tab: "Scheduler", desc: "Create scheduled report generation (monthly/quarterly/yearly) with email recipients" },
  { tab: "Compliance Score", desc: "Numeric score, letter grade (A–E), factor breakdown, and issue details" },
];

const STATUTORY_PERIOD_MODES = [
  "Monthly", "Quarterly", "Half-Yearly", "Financial Year", "Custom Range",
];

const ESI_SUMMARY_CARDS = [
  "Total Employees", "Eligible Employees", "Excluded Employees",
  "Total Gross Wages", "Total Eligible Wages",
  "Employee Contribution", "Employer Contribution", "Grand Total Contribution",
  "Average Eligible Wage",
];

const GLOBAL_PAYROLL_FILTERS = [
  { filter: "Payroll Month", desc: "Select a processed payroll run" },
  { filter: "Department", desc: "Filter by department" },
  { filter: "Calendar Month", desc: "Month input for attendance/joining/exit reports" },
  { filter: "Financial Year", desc: "Select financial year" },
  { filter: "Branch", desc: "Coming soon", disabled: true },
  { filter: "Employee Type", desc: "Coming soon", disabled: true },
  { filter: "Payroll Status", desc: "Coming soon", disabled: true },
];

const REPORT_FEATURES = [
  { feature: "Search", desc: "Text search across displayed rows — available in Leave, Payroll, ESI, and Salary Register reports" },
  { feature: "Sorting", desc: "Click any column header to toggle ascending/descending sort — available in Leave, Payroll, ESI, and Statutory reports" },
  { feature: "Pagination", desc: "Page-based navigation with Previous/Next buttons — Leave and Payroll (50 per page), Salary Register (100), Statutory and ESI (50)" },
  { feature: "Column Visibility", desc: "Checkbox dropdown to show/hide specific columns — available in Leave, Payroll, and ESI reports" },
  { feature: "Density", desc: "Display density toggle: Compact, Comfortable, or Spacious — available in Leave and Payroll reports" },
  { feature: "Favorites", desc: "Star/pin reports to a favorites bar for quick access — stored locally" },
  { feature: "Recent Reports", desc: "Up to 8 recently viewed reports shown as quick-jump buttons" },
  { feature: "Totals Row", desc: "Footer row showing column sums for monetary values — available in Payroll and ESI reports" },
];

const REPORTS_FAQS = [
  {
    q: "What is the Reports module?",
    a: "The Reports module provides a central workspace for viewing, filtering, exporting, and analysing data across Employee, Attendance, Leave, Payroll, Statutory, and Banking categories. Reports are accessed from the sidebar under \"Reports\" and organised into categories in a left navigation panel.",
  },
  {
    q: "How do I open a report?",
    a: "Click \"Reports\" in the sidebar to open the Reports workspace. Browse categories in the left navigation panel or use the search box to find a report by name. Click on a report to open it. Some reports (Employee Salary Register, ESI Report) open in dedicated pages.",
  },
  {
    q: "How do I filter a report?",
    a: "Each report has its own set of filters shown above the data grid — such as Payroll Month, Department, Date Range, or Employee. Set the desired filter values and click \"Generate\" to load the results. Global filters (Payroll Month, Department, Financial Year) apply across most payroll reports.",
  },
  {
    q: "How do I export a report?",
    a: "Export options vary by report. Look for the Excel, CSV, PDF, and Print buttons in the toolbar. Employee reports support View (modal preview) and Excel. Leave reports support Excel, CSV, PDF, and Print. Payroll reports support all four formats. Some reports require generating data before export.",
  },
  {
    q: "Can I search within report results?",
    a: "Yes — Leave, Payroll, ESI, and Salary Register reports have a \"Search rows\" text input that filters displayed rows client-side across all visible columns. The left navigation panel also has a search box to find reports by name.",
  },
  {
    q: "Can I sort report columns?",
    a: "Yes — in Leave, Payroll, ESI, and Statutory reports, click any column header to toggle ascending/descending sort. Sort indicators (arrows) show the current sort direction. Money and count columns are auto-detected for numeric sorting.",
  },
  {
    q: "How does pagination work in reports?",
    a: "Reports with many rows are paginated. Leave and Payroll reports show 50 rows per page, the Employee Salary Register shows 100 per page. Use Previous/Next buttons to navigate. The current page number, total pages, and total row count are displayed.",
  },
  {
    q: "Can I pin favourite reports?",
    a: "Yes — click the star icon next to any report in the navigation panel to pin it to your favourites bar. Favourites are stored locally in your browser. Up to 8 recently viewed reports are also shown as quick-jump buttons.",
  },
  {
    q: "Who can access reports?",
    a: "Most reports are available to Admin and HR roles. The Employee Salary Register, Statutory Reports, ESI Reports, and Banking Reports are also accessible to the Finance role. Employees do not have access to the Reports module.",
  },
  {
    q: "Why is the PDF export not working for Employee reports?",
    a: "Employee reports (Master, Joining, Exit) show the message \"PDF export is not available yet. Backend PDF generation requires approval.\" PDF export is currently supported for Attendance, Leave, Payroll, Statutory, and ESI reports.",
  },
  {
    q: "What are Statutory Report schedules?",
    a: "The Statutory Reports workspace lets you create scheduled report generation. Set a name, cadence (monthly, quarterly, or yearly), and email recipients. Scheduled reports are generated automatically and available in the Download History tab.",
  },
  {
    q: "What is the Compliance Score?",
    a: "The Compliance Score is available in the Statutory Reports workspace. It shows a numeric score, a letter grade (A through E with colour coding), a factor breakdown with progress bars, and detailed issue information to help you track statutory compliance.",
  },
  {
    q: "Why do I see 'No records found' in my report?",
    a: "This means no data matches your current filter selection. Try adjusting the payroll month, department, date range, or other filters and regenerate. For payroll reports, ensure a payroll run has been processed for the selected month. For statutory reports, data is only available from finalized payroll runs.",
  },
];

const REPORTS_ERRORS = [
  { code: "Failed to generate report. Please try again.", cause: "Report generation encountered a server error", fix: "Retry the request. If the error persists, check that the required data (payroll run, attendance, etc.) exists for the selected period" },
  { code: "Organisation context is not available.", cause: "The organisation data could not be loaded", fix: "Refresh the page. If the issue persists, ensure you are logged in and your account is linked to an organisation" },
  { code: "Generate the report before exporting.", cause: "Attempting to export without first generating the report", fix: "Click the Generate button to load report data, then use the export buttons" },
  { code: "Export failed. Please try again.", cause: "The export process encountered an error", fix: "Retry the export. Ensure the report has data and the selected format is supported for this report" },
  { code: "No records to export.", cause: "The report has no data rows to export", fix: "Adjust your filters to include data, generate the report, then export" },
  { code: "PDF export is not available yet.", cause: "PDF generation is not implemented for Employee reports", fix: "Use Excel export instead. PDF is available for Attendance, Leave, Payroll, Statutory, and ESI reports" },
  { code: "Select a payroll month first.", cause: "TDS report requires a payroll month to be selected", fix: "Choose a processed payroll run from the Payroll Month dropdown before generating" },
  { code: "Failed to generate TDS report.", cause: "The TDS report could not be generated from the selected payroll data", fix: "Ensure the selected payroll run is processed and contains TDS data" },
  { code: "Generate the report before printing.", cause: "Attempting to print before generating report data", fix: "Click Generate to load the report, then use the Print button" },
  { code: "Export failed. Ensure a payroll run is processed and try again.", cause: "Employee Salary Register export failed — no processed payroll data available", fix: "Process payroll for the selected month, then retry the export" },
  { code: "No payroll data", cause: "No processed payroll exists for the selected run", fix: "Process payroll for a month, then select that payroll run in the report filter" },
  { code: "Salary register requires a pay period.", cause: "Banking Salary Payment Register needs a pay period selected", fix: "Select a pay period before viewing the salary register in Banking reports" },
  { code: "No rows for this period. Reports read finalized payroll runs only.", cause: "Statutory reports require locked/finalized payroll data", fix: "Finalize (lock) the payroll run for the selected period, then regenerate the report" },
  { code: "No employees match the selected filters.", cause: "ESI report filters are too restrictive", fix: "Broaden your filter selection — try removing department, designation, or status filters" },
];

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function ReportsHelpPage() {
  const [search, setSearch] = useState("");
  const [openFaq, setOpenFaq] = useState(null);

  const faqRef = useRef(null);
  const q = search.trim().toLowerCase();

  const filteredFaqs = q
    ? REPORTS_FAQS.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    : REPORTS_FAQS;

  const filteredErrors = q
    ? REPORTS_ERRORS.filter(
        (e) =>
          e.code.toLowerCase().includes(q) ||
          e.cause.toLowerCase().includes(q) ||
          e.fix.toLowerCase().includes(q)
      )
    : REPORTS_ERRORS;

  const ReportTable = ({ reports, showExport }) => (
    <div className="ah-status-grid">
      {reports.map((r) => (
        <div className="ah-status-item" key={r.name}>
          <div className="ah-status-header">
            <span className="ah-status-label">{r.name}</span>
            {r.dedicated && (
              <span className="ah-info-chip" style={{ fontSize: "0.68rem" }}>
                Dedicated page
              </span>
            )}
          </div>
          <div className="ah-status-desc">{r.desc}</div>
          {r.filters && (
            <div className="ah-status-desc" style={{ marginTop: 6 }}>
              <strong>Filters:</strong> {r.filters}
            </div>
          )}
          {showExport && r.export && (
            <div className="ah-status-desc" style={{ marginTop: 4 }}>
              <strong>Export:</strong> {r.export}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="brix-page">
      {/* ---- Hero ---- */}
      <div className="hs-hero hs-hero-compact">
        <div className="hs-hero-content">
          <div className="ah-hero-title-row">
            <div className="ah-hero-icon ic-blue">
              <BarChart3 size={28} color="#1d4ed8" />
            </div>
            <div>
              <h1>Reports</h1>
              <p className="hs-hero-sub">
                View, download, and analyse reports in BrixiGo.
              </p>
            </div>
          </div>

          <form className="hs-search-wrap" onSubmit={(e) => { e.preventDefault(); if (search.trim() && faqRef.current) faqRef.current.scrollIntoView({ behavior: "smooth" }); }}>
            <div className="hs-search-input-wrap">
              <Search size={16} className="hs-search-icon" />
              <input
                type="text"
                className="hs-search-input"
                placeholder="Search reports help — e.g. payroll register, export, filter…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button type="submit" className="hs-search-btn">Search</button>
          </form>
        </div>
      </div>

      <div>
        {/* ---- 1. Overview ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Reports Overview</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The Reports module is a central workspace for viewing, filtering,
              exporting, and analysing data across all BrixiGo modules. Reports
              are accessed from the sidebar under <strong>Reports</strong> and
              organised into categories in a left navigation panel with search,
              favourites, and recent reports.
            </p>
            <p>
              Only <strong>Admin</strong> and <strong>HR</strong> roles can
              access most reports. <strong>Finance</strong> also has access to
              the Employee Salary Register, Statutory Reports, ESI Reports, and
              Banking Reports. Employees do not have access to the Reports
              module.
            </p>
          </div>
        </div>

        {/* ---- 2. Report Categories ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Layers size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Report Categories</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Reports are organised into the following categories in the
              navigation panel. Each category is displayed as a collapsible
              accordion with an item count.
            </p>
            <div className="ah-feature-grid">
              {REPORT_CATEGORIES.map((c) => (
                <div className="ah-feature-item" key={c.name}>
                  <BarChart3 size={16} color="#1a437d" />
                  <span>
                    {c.name} ({c.count})
                    {c.soon && <em style={{ color: "#94a3b8", marginLeft: 4 }}>Soon</em>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 3. Employee Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <ClipboardList size={20} className="ah-section-icon" color="#1d4ed8" />
            <h2>Employee Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Employee reports show workforce data with live preview panels
              displaying the first 5 rows. Four KPI cards are shown at the top:
              Total Employees, Active Employees, New Joiners (this month), and
              Inactive Employees.
            </p>
            <ReportTable reports={EMPLOYEE_REPORTS} showExport />
          </div>
        </div>

        {/* ---- 4. Attendance Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <CalendarCheck size={20} className="ah-section-icon" color="#15803d" />
            <h2>Attendance Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Attendance reports cover daily records, monthly summaries, absent
              tracking, late arrivals, and a full attendance register with
              day-wise status codes.
            </p>
            <ReportTable reports={ATTENDANCE_REPORTS} showExport />
            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Attendance Register Legend</div>
              <div className="ah-info-chips">
                {["P = Present", "A = Absent", "L = Leave", "H = Holiday",
                  "HD = Half Day", "PL = Paid Leave", "UL = Unpaid Leave",
                  "WO = Week Off", "WFH = WFH", "OD = On Duty"].map((s) => (
                  <span className="ah-info-chip" key={s}>{s}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 5. Leave Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <CalendarCheck size={20} className="ah-section-icon" color="#0f766e" />
            <h2>Leave Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Leave reports support search, sorting, pagination (50 per page),
              column visibility toggles, and display density options (Compact /
              Comfortable / Spacious). Report metadata shows the period,
              generated-by name, and timestamp.
            </p>
            <ReportTable reports={LEAVE_REPORTS} showExport />
          </div>
        </div>

        {/* ---- 6. Payroll Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <BarChart3 size={20} className="ah-section-icon" color="#dc2626" />
            <h2>Payroll Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Payroll reports are accessed through the Payroll Reports
              Workspace, which provides a unified interface with global filters,
              search, sorting, pagination (50 per page), column visibility,
              density controls, and a totals row for monetary columns.
            </p>
            <ReportTable reports={PAYROLL_REPORTS} showExport />
            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Global Payroll Filters</div>
              <div className="ah-status-grid">
                {GLOBAL_PAYROLL_FILTERS.map((f) => (
                  <div className="ah-status-item" key={f.filter}>
                    <div className="ah-status-header">
                      <span className={`ah-status-code ${f.disabled ? "ic-slate" : "ic-blue"}`}>
                        {f.filter.toUpperCase()}
                      </span>
                    </div>
                    <div className="ah-status-desc">
                      {f.desc}
                      {f.disabled && <em> (coming soon)</em>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 7. Statutory Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Settings size={20} className="ah-section-icon" color="#b45309" />
            <h2>Statutory Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Statutory reports are available in a dedicated workspace with five
              tabs and flexible period selection. Reports are loaded from a
              backend catalog and read only finalized payroll data.
            </p>
            <ReportTable reports={STATUTORY_REPORTS} showExport />

            <div className="ah-info-group" style={{ marginTop: 16 }}>
              <div className="ah-info-heading">Statutory Workspace Tabs</div>
              <div className="ah-status-grid">
                {STATUTORY_WORKSPACE_TABS.map((t) => (
                  <div className="ah-status-item" key={t.tab}>
                    <div className="ah-status-header">
                      <span className="ah-status-code ic-amber">{t.tab.toUpperCase()}</span>
                    </div>
                    <div className="ah-status-desc">{t.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ah-info-group" style={{ marginTop: 14 }}>
              <div className="ah-info-heading">Period Modes</div>
              <div className="ah-info-chips">
                {STATUTORY_PERIOD_MODES.map((m) => (
                  <span className="ah-info-chip" key={m}>{m}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 8. ESI Report Details ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <FileText size={20} className="ah-section-icon" color="#7c3aed" />
            <h2>ESI Report (Dedicated Workspace)</h2>
          </div>
          <div className="ah-content-card">
            <p>
              The ESI Report has its own dedicated workspace with primary
              filters, advanced filters (toggle to show/hide), three tabs
              (Report, Validations, Download History), and nine summary cards.
            </p>
            <div className="ah-info-group">
              <div className="ah-info-heading">ESI Summary Cards</div>
              <div className="ah-info-chips">
                {ESI_SUMMARY_CARDS.map((c) => (
                  <span className="ah-info-chip" key={c}>{c}</span>
                ))}
              </div>
            </div>
            <div className="ah-info-group" style={{ marginTop: 12 }}>
              <div className="ah-info-heading">Advanced Filters</div>
              <div className="ah-info-chips">
                {["Designation", "Location / Branch", "Employee", "Status",
                  "ESI Eligible", "Zero Contribution", "Contribution Period",
                  "Insurance Number", "Cost Center", "Export Format"].map((f) => (
                  <span className="ah-info-chip" key={f}>{f}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ---- 9. Banking Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <FileText size={20} className="ah-section-icon" color="#7c3aed" />
            <h2>Banking Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Banking reports are available at the Bank Disbursement Register
              page with seven tab-based views. These reports show payment
              details, status, and summaries. Accessible to Admin, HR, and
              Finance roles.
            </p>
            <ReportTable reports={BANKING_REPORTS} />
          </div>
        </div>

        {/* ---- 10. Government Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <FileText size={20} className="ah-section-icon" color="#475569" />
            <h2>Government Reports</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Government reports link to dedicated compliance workspaces for
              TDS and Form 16 generation.
            </p>
            <ReportTable reports={GOVT_REPORTS} />
          </div>
        </div>

        {/* ---- 11. Report Features ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Settings size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Report Features</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Reports offer a range of interactive features for viewing and
              analysing data. Availability varies by report type.
            </p>
            <div className="ah-status-grid">
              {REPORT_FEATURES.map((f) => (
                <div className="ah-status-item" key={f.feature}>
                  <div className="ah-status-header">
                    <span className="ah-status-code ic-blue">{f.feature.toUpperCase()}</span>
                  </div>
                  <div className="ah-status-desc">{f.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 12. Export / Download ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <Download size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Download &amp; Export</h2>
          </div>
          <div className="ah-content-card">
            <p>
              Export options vary by report. The toolbar shows available format
              buttons. Reports must be generated before exporting.
            </p>
            <div className="ah-manage-grid">
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <CheckCircle2 size={16} color="#15803d" /> Widely Supported
                </div>
                <ul className="ah-manage-list">
                  <li><strong>Excel (.xlsx)</strong> — Available across all report categories</li>
                  <li><strong>CSV</strong> — Leave, Payroll, Statutory, ESI reports</li>
                  <li><strong>Print</strong> — Leave, Payroll, ESI reports</li>
                </ul>
              </div>
              <div className="ah-manage-card">
                <div className="ah-manage-title">
                  <Info size={16} color="#1d4ed8" /> Limited Availability
                </div>
                <ul className="ah-manage-list">
                  <li><strong>PDF</strong> — Attendance, Leave (Balance/Utilization), Payroll, Statutory, ESI</li>
                  <li><strong>View (Modal)</strong> — Attendance and Employee reports only</li>
                  <li>Employee report PDF shows "not available yet"</li>
                  <li>Banking reports have no export options</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ---- 13. How to Use Reports ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <ClipboardList size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>How to Use Reports</h2>
          </div>
          <div className="ah-content-card">
            <div className="ah-workflow-steps">
              {[
                { num: "1", title: "Open Reports", desc: "Click \"Reports\" in the sidebar to open the workspace" },
                { num: "2", title: "Browse or Search", desc: "Use the left navigation panel to browse by category, or search by report name" },
                { num: "3", title: "Select a Report", desc: "Click on a report to open it. Star reports to pin them to your favourites bar" },
                { num: "4", title: "Set Filters", desc: "Configure filters (payroll month, department, date range, etc.) for the report" },
                { num: "5", title: "Generate", desc: "Click Generate to load report data. Results appear in a data grid with summary cards" },
                { num: "6", title: "Analyse", desc: "Search rows, sort columns, adjust column visibility, and review totals" },
                { num: "7", title: "Export", desc: "Download as Excel, CSV, or PDF, or use Print — depending on the report" },
              ].map((s, i) => (
                <div className="ah-workflow-step" key={s.num}>
                  <div className="ah-workflow-num">{s.num}</div>
                  {i < 6 && <div className="ah-workflow-connector" />}
                  <div className="ah-workflow-body">
                    <div className="ah-workflow-label">{s.title}</div>
                    <div className="ah-workflow-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- 14. FAQs ---- */}
        <div className="ah-section" ref={faqRef}>
          <div className="ah-section-header">
            <Info size={20} className="ah-section-icon" color="#0f2d52" />
            <h2>Frequently Asked Questions</h2>
          </div>

          {filteredFaqs.length === 0 && (
            <div className="ah-empty">No FAQs match your search.</div>
          )}

          <div className="hs-faq-list">
            {filteredFaqs.map((faq, i) => {
              const isOpen = openFaq === i;
              return (
                <div className="hs-faq-item" key={i}>
                  <button
                    className="hs-faq-q"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ display: "flex" }}
                    >
                      <ChevronDown size={18} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        className="hs-faq-a"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                      >
                        <p>{faq.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---- 15. Common Errors ---- */}
        <div className="ah-section">
          <div className="ah-section-header">
            <AlertTriangle size={20} className="ah-section-icon" color="#dc2626" />
            <h2>Common Report Issues &amp; Solutions</h2>
          </div>

          {filteredErrors.length === 0 && (
            <div className="ah-empty">No issues match your search.</div>
          )}

          <div className="ah-errors-list">
            {filteredErrors.map((err, i) => (
              <div className="ah-error-item" key={i}>
                <div className="ah-error-title">
                  <AlertTriangle size={16} />
                  <code>{err.code}</code>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Why:</span>
                  <span>{err.cause}</span>
                </div>
                <div className="ah-error-row">
                  <span className="ah-error-label">Fix:</span>
                  <span>{err.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

